import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { CommunityMemberPaymentMethod } from "@/react/types/CommunityMemberPaymentMethod";
import type {
  CardBrand,
  PaymentMethodType,
} from "@/react/types/Paywalls/PaymentMethodTypes";
import { paymentMethodTypes } from "@/react/types/Paywalls/PaymentMethodTypes";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Line } from "./Line";
import { copyComponentMap, localeRoot } from "./constants";
import type { Variant } from "./types";

const resolveType = (
  paymentMethod: CommunityMemberPaymentMethod,
): PaymentMethodType => {
  if (paymentMethod.card) {
    if (paymentMethod.card?.wallet_type === "apple_pay") {
      return paymentMethodTypes.apple_pay;
    }
    if (paymentMethod.card?.wallet_type === "google_pay") {
      return paymentMethodTypes.google_pay;
    }
    if (paymentMethod.card?.wallet_type === "link") {
      return paymentMethodTypes.link;
    }
    return paymentMethodTypes.card;
  }
  if (paymentMethod.link) {
    return paymentMethodTypes.link;
  }
  if (paymentMethod.sepa_debit) {
    return paymentMethodTypes.sepa_debit;
  }
  throw new Error(
    `Cannot resolve type for payment method: ${paymentMethod.id}`,
  );
};
const resolveCardBrand = (
  paymentMethod: CommunityMemberPaymentMethod,
): CardBrand | undefined => {
  if (paymentMethod.card) {
    return paymentMethod.card?.brand;
  }
  return undefined;
};
const resolveValue = (
  paymentMethod: CommunityMemberPaymentMethod,
): string | undefined => {
  if (paymentMethod.card) {
    return paymentMethod.card?.last4;
  }
  if (paymentMethod.link) {
    return paymentMethod.link?.email;
  }
  if (paymentMethod.sepa_debit) {
    return paymentMethod.sepa_debit?.last4;
  }
  return undefined;
};
const resolveExtra = (
  paymentMethod: CommunityMemberPaymentMethod,
): string | undefined => {
  if (paymentMethod.card) {
    return t([localeRoot, "extra", "expiring"], {
      date: paymentMethod.card?.display_expiration_date_month_and_year,
    });
  }
  return undefined;
};

export interface CommunityMemberPaymentMethodLineProps {
  className?: string;
  paymentMethod: CommunityMemberPaymentMethod;
  valueWeight?: "normal" | "medium";
  variant?: Variant;
  withCurrency?: boolean;
  withCurrencyTooltip?: boolean;
  withExtra?: boolean;
}

export const CommunityMemberPaymentMethodLine = ({
  className,
  variant = "md",
  paymentMethod,
  valueWeight = "normal",
  withCurrency = false,
  withCurrencyTooltip = false,
  withExtra = false,
}: CommunityMemberPaymentMethodLineProps) => {
  const CopyComponent = copyComponentMap[variant];

  const type = resolveType(paymentMethod);
  const cardBrand = resolveCardBrand(paymentMethod);
  const value = resolveValue(paymentMethod);
  const extra = resolveExtra(paymentMethod);

  const currencyCode = paymentMethod?.currency?.code?.toUpperCase();

  return (
    <div
      className={classNames(className, "flex flex-row items-center gap-x-2")}
    >
      <Line
        variant={variant}
        type={type}
        cardBrand={cardBrand}
        valueWeight={valueWeight}
        value={value}
      />
      {withExtra && extra && (
        <CopyComponent color="text-light">{extra}</CopyComponent>
      )}

      {withCurrency && currencyCode && (
        <CopyComponent color="text-light">({currencyCode})</CopyComponent>
      )}
      {withCurrencyTooltip && currencyCode && (
        <TippyV2
          content={t([localeRoot, "currency_tooltip"], {
            currencyCode,
          })}
          className="ml-1 inline-flex"
        >
          <Icon type="question-mark" className="!w-3" />
        </TippyV2>
      )}
    </div>
  );
};
